import React from "react";
import {
  DollarSign,
  MapPin,
  Check,
  Sparkles,
  Clock,
  Target,
} from "lucide-react";
import { Senior, Roadmap, UserPreferences } from "../../types";

interface CareerDesignPageProps {
  careerDesignStep: number;
  userPreferences: UserPreferences;
  setUserPreferences: (preferences: UserPreferences) => void;
  setCareerDesignStep: (step: number) => void;
  seniors: Senior[];
  maskName: (name: string) => string;
  selectedSeniors: number[];
  setSelectedSeniors: (seniors: number[]) => void;
  roadmaps: Roadmap[];
  selectedRoadmap: Roadmap | null;
  setSelectedRoadmap: (roadmap: Roadmap) => void;
  setCurrentPage: (page: string) => void;
}

const CareerDesignPage: React.FC<CareerDesignPageProps> = ({
  careerDesignStep,
  userPreferences,
  setUserPreferences,
  setCareerDesignStep,
  seniors,
  maskName,
  selectedSeniors,
  setSelectedSeniors,
  roadmaps,
  selectedRoadmap,
  setSelectedRoadmap,
  setCurrentPage,
}) => {
  // 희망 조건이 없으면 선배 추천 페이지로 안내
  if (!userPreferences.location && !userPreferences.jobField) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">AI 맞춤형 진로 설계</h2>
        <p className="text-gray-600 mb-6">
          진로 설계를 위해서는 먼저 희망 조건을 설정해야 합니다
        </p>
        
        <div className="bg-blue-50 p-4 rounded-xl mb-6">
          <p className="text-sm text-blue-800">
            <strong>안내:</strong> 선배 추천 기능에서 희망 조건을 입력한 후 
            AI 진로 설계를 이용해보세요.
          </p>
        </div>

        <button
          onClick={() => setCurrentPage("recommendation")}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          선배 추천으로 가기 →
        </button>
      </div>
    );
  }

  // Step 1: 조건에 맞는 선배 목록 (기존 Step 2)
  if (careerDesignStep === 1) {
    const filteredSeniors = seniors.filter((senior) => {
      return (
        (!userPreferences.location ||
          senior.location === userPreferences.location) &&
        (!userPreferences.jobField ||
          senior.jobField === userPreferences.jobField)
      );
    });

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">추천 선배 선택</h2>
        <p className="text-gray-600 mb-6">
          조건에 맞는 선배를 1명 이상 선택하세요
        </p>

        <div className="bg-blue-50 p-4 rounded-xl mb-4">
          <p className="text-sm text-blue-800">
            <strong>선택한 조건:</strong> {userPreferences.location} |{" "}
            {userPreferences.jobField}
          </p>
        </div>

        <div className="space-y-4">
          {filteredSeniors.map((senior) => (
            <div
              key={senior.id}
              className={`bg-white border-2 rounded-xl p-4 transition-all cursor-pointer
                ${
                  selectedSeniors.includes(senior.id)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200"
                }`}
              onClick={() => {
                if (selectedSeniors.includes(senior.id)) {
                  setSelectedSeniors(
                    selectedSeniors.filter((id) => id !== senior.id)
                  );
                } else {
                  setSelectedSeniors([...selectedSeniors, senior.id]);
                }
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {maskName(senior.name)}
                  </h3>
                  <p className="text-gray-600">
                    {senior.company} · {senior.position}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      {senior.salary}
                    </span>
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {senior.location}
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  {selectedSeniors.includes(senior.id) && (
                    <div className="bg-blue-500 text-white rounded-full p-1 mr-2">
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                  <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                    {senior.matchRate}% 매칭
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {senior.benefits.map((benefit, idx) => (
                  <span
                    key={idx}
                    className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                  >
                    {benefit}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="fixed bottom-20 left-0 right-0 max-w-md mx-auto px-6">
          <button
            onClick={() => setCareerDesignStep(2)}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
            disabled={selectedSeniors.length === 0}
          >
            {selectedSeniors.length}명 선택 완료 - AI 분석 시작
          </button>
        </div>
      </div>
    );
  }

  // Step 2: AI 로드맵 생성 (기존 Step 3)
  if (careerDesignStep === 2) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">AI 맞춤형 로드맵</h2>
        <p className="text-gray-600 mb-6">
          당신을 위한 최적의 진로 경로를 추천합니다
        </p>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl mb-6">
          <div className="flex items-center mb-2">
            <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
            <span className="font-semibold text-purple-800">
              AI 분석 완료
            </span>
          </div>
          <p className="text-sm text-gray-700">
            선택하신 {selectedSeniors.length}명의 선배 경로를 분석하여 최적의
            로드맵을 생성했습니다.
          </p>
        </div>

        <div className="space-y-4">
          {roadmaps.map((roadmap) => (
            <div
              key={roadmap.id}
              className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all
                ${
                  selectedRoadmap?.id === roadmap.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200"
                }`}
              onClick={() => setSelectedRoadmap(roadmap)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg flex items-center">
                    {roadmap.title}
                    {selectedRoadmap?.id === roadmap.id && (
                      <Check className="w-5 h-5 ml-2 text-blue-600" />
                    )}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {roadmap.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    {roadmap.successRate}%
                  </div>
                  <p className="text-xs text-gray-500">성공률</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {roadmap.duration}
                </span>
                <span className="flex items-center">
                  <Target className="w-4 h-4 mr-1" />
                  {roadmap.steps.length}단계
                </span>
              </div>

              {/* 간략한 타임라인 미리보기 */}
              <div className="space-y-1">
                {roadmap.steps.slice(0, 2).map((step, idx) => (
                  <div key={idx} className="flex items-center text-sm">
                    <span className="text-gray-500 w-12">{step.year}</span>
                    <div className="w-2 h-2 bg-gray-400 rounded-full mx-2"></div>
                    <span className="text-gray-700">{step.title}</span>
                  </div>
                ))}
                {roadmap.steps.length > 2 && (
                  <p className="text-xs text-gray-500 ml-16">
                    ... +{roadmap.steps.length - 2}개 단계
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="fixed bottom-20 left-0 right-0 max-w-md mx-auto px-6">
          <button
            onClick={() => {
              if (selectedRoadmap) {
                alert(
                  "로드맵이 저장되었습니다! 이제 단계별로 진행 상황을 관리할 수 있습니다."
                );
                setCurrentPage("myinfo");
              }
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
            disabled={!selectedRoadmap}
          >
            이 로드맵으로 시작하기
          </button>
        </div>
      </div>
    );
  }

  // Fallback return to ensure a valid JSX element is always returned
  return null;
};

export default CareerDesignPage;